<template>
  <v-container data-testid="saved-addresses-page" class="mt-5">
    <v-card class="pa-4" elevation="1">
      <div class="d-flex align-center justify-space-between mb-3">
        <v-card-title class="text-h5 pa-0">Saved Addresses</v-card-title>
        <v-btn
          color="primary"
          data-testid="saved-addresses-add-btn"
          @click="openAddDialog"
        >
          <v-icon start icon="add" />
          Add new address
        </v-btn>
      </div>

      <div
        v-if="loading"
        class="d-flex align-center justify-center pa-6"
        data-testid="saved-addresses-loading"
      >
        <v-progress-circular indeterminate color="primary" class="mr-3" />
        <span>Loading your saved addresses&hellip;</span>
      </div>

      <div
        v-else-if="loadError"
        class="text-center pa-6"
        data-testid="saved-addresses-error"
      >
        <v-icon size="48" color="error" icon="error_outline" class="mb-2" />
        <div class="text-body-1">
          We couldn't load your saved addresses. Please try again later.
        </div>
      </div>

      <div
        v-else-if="visibleAddresses.length === 0"
        class="text-center pa-6"
        data-testid="saved-addresses-empty"
      >
        <div class="text-body-1 text-grey-darken-1">
          No saved addresses yet. Add one to speed up checkout.
        </div>
      </div>

      <div v-else data-testid="saved-addresses-list">
        <v-card
          v-for="address in visibleAddresses"
          :key="addressId(address)"
          :data-testid="`saved-addresses-row-${addressId(address)}`"
          class="mb-3 pa-3"
          variant="outlined"
        >
          <div class="d-flex align-start justify-space-between">
            <div class="flex-grow-1">
              <div class="d-flex align-center mb-1">
                <span
                  v-if="address.label"
                  class="text-subtitle-1 font-weight-medium mr-2"
                >
                  {{ address.label }}
                </span>
                <v-chip
                  v-if="address.is_default"
                  color="success"
                  size="small"
                  :data-testid="`saved-addresses-row-${addressId(address)}-default-badge`"
                >
                  Default
                </v-chip>
              </div>
              <div class="text-body-2">
                {{ formatLine(address) }}
              </div>
            </div>
            <div class="d-flex flex-column ml-3">
              <v-btn
                v-if="!address.is_default"
                size="small"
                variant="text"
                class="mb-1"
                :data-testid="`saved-addresses-row-${addressId(address)}-set-default`"
                @click="setDefault(address)"
              >
                Set as default
              </v-btn>
              <v-btn
                size="small"
                variant="text"
                class="mb-1"
                :data-testid="`saved-addresses-row-${addressId(address)}-edit`"
                @click="openEditDialog(address)"
              >
                Edit
              </v-btn>
              <v-btn
                size="small"
                variant="text"
                color="error"
                :data-testid="`saved-addresses-row-${addressId(address)}-delete`"
                @click="confirmDelete(address)"
              >
                Delete
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </v-card>

    <!-- Add / Edit dialog -->
    <v-dialog v-model="formDialogOpen" max-width="560" persistent>
      <v-card>
        <v-card-title>
          {{ editingId ? "Edit address" : "Add new address" }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.label"
            label="Label (optional, e.g. Home, Work)"
            density="comfortable"
            data-testid="saved-addresses-form-label"
            :error-messages="errors.label"
          />
          <v-text-field
            v-model="form.recipient_name"
            label="Recipient name"
            density="comfortable"
            data-testid="saved-addresses-form-recipient"
            :error-messages="errors.recipient_name"
          />
          <v-text-field
            v-model="form.street_and_number"
            label="Street and number"
            density="comfortable"
            data-testid="saved-addresses-form-street"
            :error-messages="errors.street_and_number"
          />
          <v-text-field
            v-model="form.suite_unit_etc"
            label="Suite / Unit (optional)"
            density="comfortable"
            data-testid="saved-addresses-form-suite"
          />
          <v-text-field
            v-model="form.city"
            label="City"
            density="comfortable"
            data-testid="saved-addresses-form-city"
            :error-messages="errors.city"
          />
          <div class="d-flex">
            <v-text-field
              v-model="form.region"
              label="State / region"
              density="comfortable"
              class="mr-2"
              data-testid="saved-addresses-form-region"
              :error-messages="errors.region"
            />
            <v-text-field
              v-model="form.zip_code"
              label="ZIP / postal code"
              density="comfortable"
              data-testid="saved-addresses-form-zip"
              :error-messages="errors.zip_code"
            />
          </div>
          <v-text-field
            v-model="form.country"
            label="Country (ISO-2, e.g. US)"
            density="comfortable"
            maxlength="2"
            data-testid="saved-addresses-form-country"
            :error-messages="errors.country"
          />
          <v-checkbox
            v-model="form.is_default"
            label="Set as default address"
            density="comfortable"
            data-testid="saved-addresses-form-default"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            data-testid="saved-addresses-form-cancel"
            :disabled="saving"
            @click="closeFormDialog"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            data-testid="saved-addresses-form-save"
            @click="saveAddress"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm dialog -->
    <v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
      <v-card>
        <v-card-title>Delete address?</v-card-title>
        <v-card-text>
          This will permanently remove the address. You can re-add it later.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            variant="text"
            data-testid="saved-addresses-delete-cancel"
            :disabled="deleting"
            @click="cancelDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :loading="deleting"
            data-testid="saved-addresses-delete-confirm"
            @click="deleteAddress"
          >
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import {
  formatAddressLine,
  sortAddressesDefaultFirst,
  buildAddressPayload,
  validateAddressForm,
} from "./savedAddressesHelpers";

function emptyForm() {
  return {
    label: "",
    recipient_name: "",
    street_and_number: "",
    suite_unit_etc: "",
    city: "",
    region: "",
    zip_code: "",
    country: "US",
    is_default: false,
  };
}

export default {
  name: "StorefrontSavedAddresses",
  data() {
    return {
      addresses: [],
      loading: false,
      loadError: false,
      saving: false,
      deleting: false,
      formDialogOpen: false,
      deleteDialogOpen: false,
      editingId: null,
      pendingDelete: null,
      form: emptyForm(),
      errors: {},
    };
  },
  computed: {
    visibleAddresses() {
      return sortAddressesDefaultFirst(this.addresses);
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    addressId(address) {
      if (!address) return null;
      if (address._jv && address._jv.id != null) return address._jv.id;
      if (address.id != null) return address.id;
      return null;
    },
    formatLine(address) {
      return formatAddressLine(address);
    },
    async load() {
      this.loading = true;
      this.loadError = false;
      try {
        // jv/get takes a bare string (or a 2-element [type, config] array).
        // A single-element array ["addresses"] leaves config=undefined and
        // crashes in jsonapi-vuex (PR #1836 crash class); use the string form
        // like the sibling StorefrontOrdersList.vue.
        const result = await this.$store.dispatch("jv/get", "addresses");
        const fetched = Object.values(result || {});
        // Defense in depth (CLAUDE.md Principle 5).
        // Backend /api/addresses/ scopes by account_id via Shopper role policy
        // (api/src/models/mixins/roles.py _campaign_perms — Shopper branch).
        // We additionally filter client-side so a future backend regression
        // can't silently leak other shoppers' addresses through this page.
        const myAccountId = this.$store.state.account?._jv?.id;
        this.addresses = myAccountId
          ? fetched.filter(
              (a) =>
                String(a?.account?._jv?.id ?? a?.account_id ?? "") ===
                String(myAccountId),
            )
          : fetched;
      } catch (err) {
        this.loadError = true;
        // eslint-disable-next-line no-console
        console.error("Failed to load saved addresses", describeError(err));
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.editingId = null;
      this.form = emptyForm();
      this.errors = {};
      this.formDialogOpen = true;
    },
    openEditDialog(address) {
      this.editingId = this.addressId(address);
      this.form = {
        label: address.label || "",
        recipient_name: address.recipient_name || "",
        street_and_number: address.street_and_number || "",
        suite_unit_etc: address.suite_unit_etc || "",
        city: address.city || "",
        region: address.region || "",
        zip_code: address.zip_code || "",
        country: address.country || "US",
        is_default: !!address.is_default,
      };
      this.errors = {};
      this.formDialogOpen = true;
    },
    closeFormDialog() {
      if (this.saving) return;
      this.formDialogOpen = false;
      this.editingId = null;
      this.form = emptyForm();
      this.errors = {};
    },
    async saveAddress() {
      const validation = validateAddressForm(this.form);
      if (!validation.valid) {
        this.errors = validation.errors;
        return;
      }
      this.errors = {};
      this.saving = true;
      const payload = buildAddressPayload(this.form);
      try {
        if (this.editingId) {
          await this.$store.dispatch("jv/patch", [
            { ...payload, _jv: { id: this.editingId, type: "Address" } },
            { url: `/addresses/${this.editingId}` },
          ]);
          this.$store.dispatch("raiseSuccess", "Address updated");
        } else {
          await this.$store.dispatch("jv/post", [
            { ...payload, _jv: { type: "Address" } },
            { url: "/addresses" },
          ]);
          this.$store.dispatch("raiseSuccess", "Address added");
        }
        this.formDialogOpen = false;
        this.editingId = null;
        this.form = emptyForm();
        await this.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save address", describeError(err));
        this.$store.dispatch(
          "raiseError",
          "Could not save the address. Please try again.",
        );
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(address) {
      this.pendingDelete = address;
      this.deleteDialogOpen = true;
    },
    cancelDelete() {
      if (this.deleting) return;
      this.deleteDialogOpen = false;
      this.pendingDelete = null;
    },
    async deleteAddress() {
      if (!this.pendingDelete) return;
      this.deleting = true;
      try {
        const id = this.addressId(this.pendingDelete);
        await this.$store.dispatch("jv/delete", [
          { _jv: { id, type: "Address" } },
          { url: `/addresses/${id}` },
        ]);
        this.$store.dispatch("raiseSuccess", "Address deleted");
        this.deleteDialogOpen = false;
        this.pendingDelete = null;
        await this.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to delete address", describeError(err));
        this.$store.dispatch(
          "raiseError",
          "Could not delete the address. Please try again.",
        );
      } finally {
        this.deleting = false;
      }
    },
    async setDefault(address) {
      // Backend enforces single is_default=true invariant — PATCHing
      // is_default: true demotes the prior default automatically. No
      // separate set_default endpoint needed.
      const id = this.addressId(address);
      try {
        await this.$store.dispatch("jv/patch", [
          { is_default: true, _jv: { id, type: "Address" } },
          { url: `/addresses/${id}` },
        ]);
        this.$store.dispatch("raiseSuccess", "Default address updated");
        await this.load();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to set default address", describeError(err));
        this.$store.dispatch(
          "raiseError",
          "Could not set default address. Please try again.",
        );
      }
    },
  },
};
</script>

<style scoped>
</style>
